import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, Phone, MessageCircle, Mail } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema } from "@/lib/schema";
import { getActiveProvince } from "@/provinces/_registry";
import { COMPANY } from "@/lib/company";
import { CATALOG_GROUPS } from "@/lib/catalog";
import { SERVICE_PRICE_FROM } from "@/content/service-pillars/price-from.gen";
import type { ServicePillarMeta } from "@/content/service-pillars/meta.gen";

export const Route = createFileRoute("/en/catalog")({
  loader: async (): Promise<{ items: ServicePillarMeta[] }> => {
    const { SERVICE_PILLAR_META } = await import("@/content/service-pillars/meta");
    return { items: SERVICE_PILLAR_META };
  },
  head: ({ loaderData }) => {
    const items = loaderData?.items ?? [];
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `Service Catalog — ${items.length} Services | Thai Notary ${config.nameEn}`;
    const description = `Searchable directory of all ${items.length} Thai Notary services — notarial services, certified and NAATI translation, MFA and embassy legalization, police clearance, visas and marriage registration. Filter by category and open each service for documents, steps and timelines.`;
    const base = pageHeadForProvince({
      title,
      description,
      path: "/en/catalog",
      alternates: [{ lang: "th", path: "/catalog" }],
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Service Catalog", url: `${site}/en/catalog` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": `${site}/en/catalog#collection`,
            url: `${site}/en/catalog`,
            name: title,
            description,
            inLanguage: "en",
            isPartOf: { "@id": `${site}/#website` },
            about: { "@id": `${site}/#organization` },
            mainEntity: {
              "@type": "ItemList",
              name: "Thai Notary service catalog",
              numberOfItems: items.length,
              itemListElement: items.map((s, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: s.h1,
                url: `${site}/services/${s.slug}`,
              })),
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            "@id": `${site}/en/catalog#offercatalog`,
            name: `All services by ${COMPANY.brandName}`,
            url: `${site}/en/catalog`,
            provider: { "@id": `${site}/#organization` },
            itemListElement: CATALOG_GROUPS.map((g) => ({
              "@type": "OfferCatalog",
              name: g.labelEn,
              description: g.descEn ?? g.labelEn,
              itemListElement: items
                .filter((s) => s.category === g.id)
                .map((s) => {
                  const from = SERVICE_PRICE_FROM[s.slug];
                  return {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: s.h1,
                      description: s.metaDesc,
                      url: `${site}/services/${s.slug}`,
                      serviceType: g.labelEn,
                      provider: { "@id": `${site}/#organization` },
                    },
                    availability: "https://schema.org/InStock",
                    url: `${site}/services/${s.slug}`,
                    ...(from
                      ? {
                          price: from,
                          priceCurrency: "THB",
                          priceSpecification: {
                            "@type": "PriceSpecification",
                            price: from,
                            priceCurrency: "THB",
                            valueAddedTaxIncluded: false,
                            description: "Starting price",
                          },
                        }
                      : {}),
                  };
                }),
            })),
          }),
        },
      ],
    };
  },
  component: EnCatalogPage,
});

function EnCatalogPage() {
  const { items } = Route.useLoaderData() as { items: ServicePillarMeta[] };
  const { config } = getActiveProvince();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string>("all");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((s) => {
      if (cat !== "all" && s.category !== cat) return false;
      if (!needle) return true;
      return (
        s.h1.toLowerCase().includes(needle) ||
        s.metaTitle.toLowerCase().includes(needle) ||
        s.metaDesc.toLowerCase().includes(needle) ||
        s.slug.toLowerCase().includes(needle)
      );
    });
  }, [items, q, cat]);

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of items) map[s.category] = (map[s.category] ?? 0) + 1;
    return map;
  }, [items]);

  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-6xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <span>Service Catalog</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Service Catalog — All {items.length} Legal, Notarial and Document Services in {config.nameEn}
        </h1>
        <p className="mb-6 max-w-3xl text-muted-foreground" data-speakable="true">
          One searchable directory covering every service we deliver: notarial certification by a Notarial Services
          Attorney registered with the Lawyers Council of Thailand, certified and NAATI translation, legalization at
          the Department of Consular Affairs and foreign embassies, police clearance certificates, visas, work permits
          and Thai–foreign marriage registration. Fees depend on document type, page count, language and the receiving
          authority, so quotations are given by our staff by phone, LINE or email after reviewing your documents.
        </p>

        <section aria-label="Search services" className="mb-8 rounded-xl border bg-card p-5">
          <label htmlFor="en-catalog-search" className="mb-2 block text-sm font-semibold">
            Search services
          </label>
          <div className="relative">
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id="en-catalog-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. notary, NAATI, apostille, police clearance, marriage"
              className="w-full rounded-lg border bg-background py-3 pl-10 pr-3 text-sm outline-none focus:border-primary"
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCat("all")}
              aria-pressed={cat === "all"}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${cat === "all" ? "border-primary text-primary" : "bg-secondary/60"}`}
            >
              All ({items.length})
            </button>
            {CATALOG_GROUPS.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setCat(g.id)}
                aria-pressed={cat === g.id}
                className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${cat === g.id ? "border-primary text-primary" : "bg-secondary/60"}`}
              >
                {g.labelEn} ({counts[g.id] ?? 0})
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground" role="status">
            Showing {filtered.length} of {items.length} services
          </p>
        </section>

        {CATALOG_GROUPS.filter((g) => cat === "all" || cat === g.id).map((g) => {
          const rows = filtered.filter((s) => s.category === g.id);
          if (rows.length === 0) return null;
          return (
            <section key={g.id} id={`cat-${g.id}`} className="mb-10" aria-labelledby={`en-h-${g.id}`}>
              <h2 id={`en-h-${g.id}`} className="text-xl font-bold">
                {g.labelEn} <span className="text-sm font-medium text-muted-foreground">({rows.length})</span>
              </h2>
              <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{g.descEn}</p>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {rows.map((s) => (
                  <li key={s.slug} className="rounded-lg border bg-card p-4">
                    <h3 className="text-sm font-semibold leading-snug">
                      <a href={`/services/${s.slug}`} className="hover:underline">
                        {s.metaTitle.split("|")[0].trim()}
                      </a>
                    </h3>
                    <p className="mt-1 line-clamp-4 text-sm text-muted-foreground">{s.metaDesc}</p>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        <section aria-labelledby="en-catalog-quote" className="rounded-xl border bg-card p-6">
          <h2 id="en-catalog-quote" className="text-lg font-bold">
            Request a quotation and document review
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Send scans or photos of your documents and we will confirm in writing what is required, the exact
            sequence of certifications, the realistic timeline and the total cost before any work starts.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a href={`tel:${COMPANY.phoneIntl}`} className="inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
            <a href={`https://line.me/R/ti/p/${COMPANY.lineId}`} target="_blank" rel="noopener" className="inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold">
              <MessageCircle className="h-4 w-4" /> LINE {COMPANY.lineId}
            </a>
            <a href={`mailto:${COMPANY.email}`} className="inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold">
              <Mail className="h-4 w-4" /> {COMPANY.email}
            </a>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
